
import { createReducer } from '@reduxjs/toolkit';
import {
    REQUEST_NEWS_ARCHIVE, RECEIVE_NEWS_ARCHIVE, FAIL_NEWS_ARCHIVE,
} from '../actions/NewsArchiveActions';
import {
    SET_DATA_HOME, SET_PINNED_RIGHT_COLUMN_NEWS, SET_DATA_PAGE, SET_ISLOADING, SET_NEWS_CATEGORY,
    DATA_SINGLE_NEWS, SET_DATA_MOST_READ, IS_FETCHING_NEWS_SINGLE,
    END_FETCHING_NEWS_SINGLE, SET_DATA_RELATED_NEWS,
    SET_LAST_TAGS, SET_NOT_FOUND, SET_NEWS_FILTERED,
    RESET_NEWS_FILTERED, SET_CURRENT_CATEGORY_SLUG, SET_NOTICE_TEXT,
    SET_DATA_NEWS_TAG, SET_CURRENT_TAG_SLUG, RESET_NEWS_DATA_TAG,
    NEW_NEWS, UPDATE_NEWS_DATA, SET_TAGS,
    NEW_NEWS_FILTERED, UPDATE_NEWS_FILTERED_DATA, UPDATE_NEWS_VOTES,
    INSERT_NEWS_VIEW, RESET_NEWS_VIEWS,
} from '../actions/NewsActions';

const initialState = {
    archivePages: {},
    newsViews: [],
    newsHomeData: [],
    newsHomeDataUpdated: false,
    pinnedRightColumnNews: null,
    newsPageData: [],
    newsPageDataUpdated: false,
    newsTagData: [],
    newsCategories: [],
    dataSingleNews: {},
    dataNewsMostRead: [],
    dataRelatedNews: [],
    lastTags: '',
    newsFiltered: [],
    newsFilteredUpdated: false,
    dataTags: [],
    currentCategorySlug: '',
    currentTagSlug: '',
    currentTagTitle: '',
    noticeText: '',
    isFetching: true,
    notFound: false,
    isLoading: false,
    lastSlug: null,
};

// Returns the index of the news item with the given `id` inside `list`, or -1 if not found.
const findNewsIndexById = (list, id) => list.findIndex((item) => item.id === id);

const news = createReducer(initialState, (builder) => {
    builder
        .addCase(REQUEST_NEWS_ARCHIVE, (state, action) => {
            state.archivePages[action.key] = { loading: true };
        })
        .addCase(RECEIVE_NEWS_ARCHIVE, (state, action) => {
            state.archivePages[action.key] = action.data;
        })
        .addCase(FAIL_NEWS_ARCHIVE, (state, action) => {
            state.archivePages[action.key] = { error: true };
        })
        .addCase(SET_DATA_HOME, (state, action) => {
            state.newsHomeData = action.data;
            state.newsHomeDataUpdated = Date.now();
        })
        .addCase(SET_PINNED_RIGHT_COLUMN_NEWS, (state, action) => {
            state.pinnedRightColumnNews = action.data || null;
        })
        .addCase(SET_DATA_PAGE, (state, action) => {
            state.newsPageData = state.newsPageData.concat(action.data);
            state.newsPageDataUpdated = Date.now();
        })
        .addCase(UPDATE_NEWS_DATA, (state, action) => {
            // action.data is always the full, already-deduplicated news array (built in
            // NewsActions.js's _removeNewNewsProperty from the current store data), not a
            // delta - replace, don't append (the old Immutable code tried to .concat() here,
            // which actually crashed on dispatch due to an unrelated Immutable.get() key bug).
            state.newsPageData = action.data;
            state.newsPageDataUpdated = Date.now();
        })
        .addCase(UPDATE_NEWS_FILTERED_DATA, (state, action) => {
            state.newsFiltered = action.data;
            state.newsFilteredUpdated = Date.now();
        })
        .addCase(SET_DATA_NEWS_TAG, (state, action) => {
            state.currentTagTitle = action.data.tagMeta && action.data.tagMeta.title;
            state.newsTagData = state.newsTagData.concat(action.data.newsArray);
        })
        .addCase(SET_CURRENT_TAG_SLUG, (state, action) => {
            state.currentTagSlug = action.currentTagSlug;
        })
        .addCase(RESET_NEWS_DATA_TAG, (state) => {
            state.newsTagData = [];
        })
        .addCase(SET_ISLOADING, (state, action) => {
            state.isLoading = action.isLoading;
        })
        .addCase(DATA_SINGLE_NEWS, (state, action) => {
            state.dataSingleNews = action.dataSingleNews;
        })
        .addCase(SET_NEWS_CATEGORY, (state, action) => {
            state.newsCategories = action.data;
        })
        .addCase(SET_TAGS, (state, action) => {
            state.dataTags = action.data;
        })
        .addCase(SET_NEWS_FILTERED, (state, action) => {
            const mergedNews = [...state.newsFiltered, ...action.data];

            state.newsFiltered = Array.from(
                new Map(mergedNews.map((item) => [item.id, item])).values(),
            );
            state.newsFilteredUpdated = Date.now();
        })
        .addCase(RESET_NEWS_FILTERED, (state) => {
            state.newsFiltered = [];
        })
        .addCase(SET_CURRENT_CATEGORY_SLUG, (state, action) => {
            state.currentCategorySlug = action.currentCategorySlug;
        })
        .addCase(SET_DATA_MOST_READ, (state, action) => {
            state.dataNewsMostRead = action.dataNewsMostRead;
        })
        .addCase(IS_FETCHING_NEWS_SINGLE, (state) => {
            state.isFetching = true;
        })
        .addCase(END_FETCHING_NEWS_SINGLE, (state, action) => {
            state.isFetching = false;

            if (action.slug) {
                // We only update the lastSlug prop if the slug from payload is not empty.
                // The slug will most likely be empty when an error occurs when trying to fetch news.
                // So, the lastSlug prop is storing the slug of the last successful attempt.
                state.lastSlug = action.slug;
            }
        })
        .addCase(SET_DATA_RELATED_NEWS, (state, action) => {
            state.dataRelatedNews = action.dataRelatedNews;
        })
        .addCase(SET_LAST_TAGS, (state, action) => {
            state.lastTags = action.lastTags;
        })
        .addCase(SET_NOT_FOUND, (state, action) => {
            state.notFound = action.notFound;
        })
        .addCase(SET_NOTICE_TEXT, (state, action) => {
            state.noticeText = action.noticeText;
        })
        .addCase(NEW_NEWS, (state, action) => {
            action.data.reverse().forEach((item) => {
                if (item.featuredImage) {
                    state.newsPageData.splice(0, 0, { ...item, newNews: true });
                } else {
                    state.newsPageData.splice(1, 0, { ...item, newNews: true });
                }
            });
            state.newsPageDataUpdated = Date.now();
        })
        .addCase(NEW_NEWS_FILTERED, (state, action) => {
            action.data.forEach((item) => {
                state.newsFiltered.splice(0, 0, { ...item, newNews: true });
            });
            state.newsFilteredUpdated = Date.now();
        })
        .addCase(UPDATE_NEWS_VOTES, (state, action) => {
            const listKeys = ['newsPageData', 'newsFiltered', 'newsHomeData', 'dataNewsMostRead'];

            listKeys.forEach((key) => {
                const index = findNewsIndexById(state[key], action.id);

                if (index !== -1) {
                    state[key][index] = { ...state[key][index], ...action.data };
                }
            });

            state.dataSingleNews = { ...state.dataSingleNews, ...action.data };
        })
        .addCase(INSERT_NEWS_VIEW, (state, action) => {
            state.newsViews.push(action.newsView);
        })
        .addCase(RESET_NEWS_VIEWS, (state) => {
            state.newsViews = [];
        });
});

export default news;
